import {
  AppstoreOutlined,
  DashboardOutlined,
  ProjectOutlined,
  UserOutlined,
} from "@ant-design/icons";
import { FC } from "react";

export type HomeDashboardView =
  | "workspace"
  | "board"
  | "personalBoard"
  | "profile"
  | "projectFlow";

type SidebarNavItem = {
  key: Exclude<HomeDashboardView, "projectFlow">;
  label: string;
  description: string;
  icon: JSX.Element;
};

const navigationItems: SidebarNavItem[] = [
  {
    key: "workspace",
    label: "工作台",
    description: "总览与协作节奏",
    icon: <AppstoreOutlined />,
  },
  {
    key: "board",
    label: "项目看板",
    description: "项目状态与任务分布",
    icon: <ProjectOutlined />,
  },
  {
    key: "personalBoard",
    label: "个人看板",
    description: "私人卡片与倒计时",
    icon: <DashboardOutlined />,
  },
  {
    key: "profile",
    label: "个人资料",
    description: "账号与成员信息",
    icon: <UserOutlined />,
  },
];

interface HomeSidebarProps {
  activeView: HomeDashboardView;
  brandMark: string;
  brandSubtitle: string;
  brandTitle: string;
  completed: number;
  dueTasks?: string[];
  isTeamAdmin: boolean;
  pending: number;
  showDueTasks: boolean;
  statusText: string;
  statusTitle: string;
  onChangeView: (view: Exclude<HomeDashboardView, "projectFlow">) => void;
}

const HomeSidebar: FC<HomeSidebarProps> = ({
  activeView,
  brandMark,
  brandSubtitle,
  brandTitle,
  completed,
  dueTasks = [],
  isTeamAdmin,
  pending,
  showDueTasks,
  statusText,
  statusTitle,
  onChangeView,
}) => {
  const navItems = navigationItems.map((item) =>
    !isTeamAdmin && item.key === "board"
      ? {
          ...item,
          label: "我的项目",
          description: "参与项目与任务进度",
        }
      : item
  );

  return (
    <aside className="workspaceSidebar">
      <div className="workspaceBrand">
        <div className="workspaceBrand__mark">{brandMark}</div>
        <div>
          <strong>{brandTitle}</strong>
          <span>{brandSubtitle}</span>
        </div>
      </div>

      <div className="workspaceSidebar__section">
        <span className="workspaceSidebar__label">导航</span>
        <nav className="workspaceNav">
          {navItems.map((item) => (
            <button
              key={item.key}
              className={
                activeView === item.key ||
                (activeView === "projectFlow" && item.key === "board")
                  ? "workspaceNav__item active"
                  : "workspaceNav__item"
              }
              onClick={() => onChangeView(item.key)}
              type="button"
            >
              <span className="workspaceNav__icon">{item.icon}</span>
              <span className="workspaceNav__content">
                <strong>{item.label}</strong>
                <small>{item.description}</small>
              </span>
            </button>
          ))}
        </nav>
      </div>

      <div className="workspaceSidebar__meta">
        <span className="workspaceSidebar__label">今日状态</span>
        <div className="workspaceSidebar__card">
          <strong>{statusTitle}</strong>
          <div className="workspaceSidebar__progress">
            <span>完成 {completed}</span>
            <span>待完成 {pending}</span>
          </div>
          <p>{statusText}</p>
          {showDueTasks && dueTasks.length > 0 && (
            <div className="workspaceSidebar__tasks">
              <div className="workspaceSidebar__taskHeader">
                <span>本周待办</span>
                <strong>{dueTasks.length}</strong>
              </div>
              <ul
                aria-label={`本周待办任务，共 ${dueTasks.length} 项`}
                className="workspaceSidebar__taskList"
              >
                {dueTasks.map((task, index) => (
                  <li key={`${task}-${index}`} title={task}>{task}</li>
                ))}
              </ul>
            </div>
          )}
        </div>
      </div>
    </aside>
  );
};

export default HomeSidebar;
